import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Paperclip,
  X,
  Loader2,
  User,
  Stethoscope,
  CalendarRange,
  Users,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes } from 'firebase/storage';
import { staffingFunctions, staffingStorage } from '../firebase';
import lumosyeLogo from '../assets/Lumosye-staffing.png';

/* ─────────────────── CONSTANTS ────────────────────────────────────── */
const EMAIL = 'contact@lumosye.com';

/* ─────────────────── STEPS CONFIG ─────────────────────────────────── */
const STEPS = [
  { id: 'personal',      label: 'Personal Info',       icon: User },
  { id: 'specialty',     label: 'Specialty & Licensure', icon: Stethoscope },
  { id: 'availability',  label: 'Availability',         icon: CalendarRange },
  { id: 'references',    label: 'References',           icon: Users },
  { id: 'malpractice',   label: 'Final Step',           icon: ShieldCheck },
];

/* ─────────────────── FIELD PRIMITIVES ─────────────────────────────── */
const iCls =
  'w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white ' +
  'placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60 ' +
  'focus:ring-1 focus:ring-[#c9a84c]/30 transition';
const sCls =
  'w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white ' +
  'focus:outline-none focus:border-[#c9a84c]/60 focus:ring-1 focus:ring-[#c9a84c]/30 ' +
  'transition appearance-none';

function Field({ label, required, hint, children, half }) {
  return (
    <div className={`flex flex-col gap-1.5 ${half ? '' : 'col-span-2'}`}>
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {label}
        {required && <span className="text-[#c9a84c] ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function SectionHeader({ step, title, subtitle }) {
  const Icon = STEPS[step].icon;
  return (
    <div className="mb-8">
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#c9a84c]/10 border border-[#c9a84c]/20 mb-4">
        <Icon size={20} className="text-[#c9a84c]" />
      </div>
      <h2
        className="text-2xl font-black text-white mb-1"
        style={{ fontFamily: "'Georgia', serif" }}
      >
        {title}
      </h2>
      <p className="text-sm text-slate-400">{subtitle}</p>
    </div>
  );
}

/* ─────────────────── PROGRESS BAR ─────────────────────────────────── */
function ProgressBar({ current, total }) {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-3">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center">
            <div
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${
                i < current
                  ? 'bg-[#c9a84c] text-[#040d1c]'
                  : i === current
                  ? 'bg-[#c9a84c]/20 border border-[#c9a84c] text-[#c9a84c]'
                  : 'bg-[#1e2d4a] text-slate-500'
              }`}
            >
              {i < current ? <CheckCircle2 size={14} /> : i + 1}
            </div>
            {i < total - 1 && (
              <div className="hidden sm:block h-px w-12 md:w-16 lg:w-20 mx-1 transition-all"
                style={{ background: i < current ? '#c9a84c' : '#1e2d4a' }} />
            )}
          </div>
        ))}
      </div>
      <div className="h-1 w-full rounded-full bg-[#1e2d4a] overflow-hidden">
        <div
          className="h-full rounded-full bg-[#c9a84c] transition-all duration-500"
          style={{ width: `${((current) / (total - 1)) * 100}%` }}
        />
      </div>
      <p className="text-xs text-slate-500 mt-2">
        Step {current + 1} of {total} — {STEPS[current].label}
      </p>
    </div>
  );
}

/* ─────────────────── STEP COMPONENTS ──────────────────────────────── */

function StepPersonal({ form, set, cvFile, setCvFile, fileInputRef }) {
  return (
    <>
      <SectionHeader
        step={0}
        title="Personal Information"
        subtitle="Tell us about yourself and how to reach you."
      />
      <div className="grid grid-cols-2 gap-4">
        <Field label="First Name" required half>
          <input className={iCls} placeholder="James" required
            value={form.firstName || ''} onChange={(e) => set('firstName', e.target.value)} />
        </Field>
        <Field label="Last Name" required half>
          <input className={iCls} placeholder="Osei" required
            value={form.lastName || ''} onChange={(e) => set('lastName', e.target.value)} />
        </Field>
        <Field label="Email Address" required>
          <input type="email" className={iCls} placeholder="you@email.com" required
            value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
        </Field>
        <Field label="Phone Number" required>
          <input type="tel" className={iCls} placeholder="(713) 000-0000" required
            value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} />
        </Field>
        <Field label="Current City" required half>
          <input className={iCls} placeholder="Houston" required
            value={form.city || ''} onChange={(e) => set('city', e.target.value)} />
        </Field>
        <Field label="State" required half>
          <input className={iCls} placeholder="TX" required
            value={form.state || ''} onChange={(e) => set('state', e.target.value)} />
        </Field>
        <Field label="Credential / Degree" required>
          <select className={sCls} required
            value={form.credential || ''} onChange={(e) => set('credential', e.target.value)}>
            <option value="" disabled>Select your credential</option>
            <option>MD</option>
            <option>DO</option>
            <option>NP</option>
            <option>PA</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Upload CV / Resume" hint="PDF or Word (.docx) · Max 10MB">
          <label className={`flex items-center gap-3 cursor-pointer rounded-xl border border-dashed px-4 py-3 transition ${
            cvFile ? 'border-[#c9a84c]/60 bg-[#c9a84c]/5' : 'border-[#1e2d4a] hover:border-[#c9a84c]/40 bg-[#070f1f]'
          }`}>
            <Paperclip size={15} className={cvFile ? 'text-[#c9a84c]' : 'text-slate-500'} />
            <span className={`text-sm truncate ${cvFile ? 'text-[#c9a84c]' : 'text-slate-500'}`}>
              {cvFile ? cvFile.name : 'Choose file'}
            </span>
            {cvFile && (
              <button type="button" onClick={(e) => { e.preventDefault(); setCvFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="ml-auto text-slate-500 hover:text-white transition">
                <X size={14} />
              </button>
            )}
            <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && f.size <= 10 * 1024 * 1024) setCvFile(f);
                else if (f) alert('File exceeds 10MB. Please upload a smaller file.');
              }} />
          </label>
        </Field>
      </div>
    </>
  );
}

function StepSpecialty({ form, set }) {
  return (
    <>
      <SectionHeader
        step={1}
        title="Specialty & Licensure"
        subtitle="Your clinical background and active licenses."
      />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Primary Specialty" required>
          <select className={sCls} required
            value={form.specialty || ''} onChange={(e) => set('specialty', e.target.value)}>
            <option value="" disabled>Select specialty</option>
            <option>Hospital Medicine (Hospitalist)</option>
            <option>Emergency Medicine</option>
            <option>Internal Medicine</option>
            <option>Family Medicine</option>
            <option>Critical Care / ICU</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Years of Experience" required>
          <select className={sCls} required
            value={form.yearsExperience || ''} onChange={(e) => set('yearsExperience', e.target.value)}>
            <option value="" disabled>Select range</option>
            <option>Less than 1 year</option>
            <option>1–3 years</option>
            <option>3–5 years</option>
            <option>5–10 years</option>
            <option>10+ years</option>
          </select>
        </Field>
        <Field label="States Licensed" required hint="List all states where you hold an active medical license">
          <input className={iCls} placeholder="TX, AZ, MN, IL…" required
            value={form.licensedStates || ''} onChange={(e) => set('licensedStates', e.target.value)} />
        </Field>
        <Field label="DEA License Number" hint="Optional — include if applicable">
          <input className={iCls} placeholder="AB1234567"
            value={form.deaNumber || ''} onChange={(e) => set('deaNumber', e.target.value)} />
        </Field>
        <Field label="Board Certification" required>
          <select className={sCls} required
            value={form.boardCertified || ''} onChange={(e) => set('boardCertified', e.target.value)}>
            <option value="" disabled>Select status</option>
            <option>Board Certified (ABIM)</option>
            <option>Board Certified (ABEM)</option>
            <option>Board Certified (ABFM)</option>
            <option>Board Certified — Other</option>
            <option>Board Eligible</option>
            <option>Not Board Certified</option>
          </select>
        </Field>
        <Field label="NPI Number" hint="Optional">
          <input className={iCls} placeholder="1234567890"
            value={form.npiNumber || ''} onChange={(e) => set('npiNumber', e.target.value)} />
        </Field>
        <Field label="Additional Certifications / Skills" hint="e.g. ACLS, BLS, ATLS, ultrasound, procedures">
          <textarea rows={3} className={iCls}
            placeholder="List any additional certifications or clinical skills relevant to locum work…"
            value={form.additionalCerts || ''} onChange={(e) => set('additionalCerts', e.target.value)} />
        </Field>
      </div>
    </>
  );
}

function StepAvailability({ form, set }) {
  return (
    <>
      <SectionHeader
        step={2}
        title="Availability & Preferences"
        subtitle="Help us match you with the right assignments."
      />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Availability" required>
          <select className={sCls} required
            value={form.availability || ''} onChange={(e) => set('availability', e.target.value)}>
            <option value="" disabled>Select availability</option>
            <option>Immediately available</option>
            <option>Within 30 days</option>
            <option>1–3 months</option>
            <option>Flexible / exploring options</option>
          </select>
        </Field>
        <Field label="Shift Preference" required>
          <select className={sCls} required
            value={form.shiftPreference || ''} onChange={(e) => set('shiftPreference', e.target.value)}>
            <option value="" disabled>Select preference</option>
            <option>Day shifts</option>
            <option>Nights / Nocturnist</option>
            <option>Block schedules</option>
            <option>Short-notice / flexible</option>
            <option>Open to all</option>
          </select>
        </Field>
        <Field label="Preferred Assignment Length" required>
          <select className={sCls} required
            value={form.assignmentLength || ''} onChange={(e) => set('assignmentLength', e.target.value)}>
            <option value="" disabled>Select preference</option>
            <option>Short-term (1–4 weeks)</option>
            <option>Medium-term (1–3 months)</option>
            <option>Long-term (3+ months)</option>
            <option>Open to all</option>
          </select>
        </Field>
        <Field label="Willing to Travel?" required>
          <select className={sCls} required
            value={form.willingToTravel || ''} onChange={(e) => set('willingToTravel', e.target.value)}>
            <option value="" disabled>Select</option>
            <option>Yes — anywhere</option>
            <option>Yes — within my region</option>
            <option>Prefer local only</option>
          </select>
        </Field>
        <Field label="Geographic Preferences" hint="States or regions you prefer or want to avoid">
          <textarea rows={2} className={iCls}
            placeholder="e.g. Prefer Southeast, open to TX and AZ, avoid Northeast…"
            value={form.geoPreferences || ''} onChange={(e) => set('geoPreferences', e.target.value)} />
        </Field>
        <Field label="Minimum Compensation Expectation" hint="Optional — helps us match the right facilities">
          <input className={iCls} placeholder="e.g. $200/hr or $12,000/week"
            value={form.compensationExpectation || ''} onChange={(e) => set('compensationExpectation', e.target.value)} />
        </Field>
        <Field label="Anything else about your availability or preferences?">
          <textarea rows={3} className={iCls}
            placeholder="Blackout dates, scheduling constraints, facility type preferences…"
            value={form.availabilityNotes || ''} onChange={(e) => set('availabilityNotes', e.target.value)} />
        </Field>
      </div>
    </>
  );
}

function StepReferences({ form, set }) {
  return (
    <>
      <SectionHeader
        step={3}
        title="Professional References"
        subtitle="Two professional references who can speak to your clinical work."
      />
      <div className="space-y-6">
        {[1, 2].map((n) => (
          <div key={n} className="rounded-2xl border border-[#1e2d4a] bg-[#071325]/60 p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-[#c9a84c] mb-4">
              Reference {n}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full Name" required half>
                <input className={iCls} placeholder="Dr. Sarah Johnson" required
                  value={form[`ref${n}Name`] || ''} onChange={(e) => set(`ref${n}Name`, e.target.value)} />
              </Field>
              <Field label="Title / Role" required half>
                <input className={iCls} placeholder="Chief of Medicine" required
                  value={form[`ref${n}Title`] || ''} onChange={(e) => set(`ref${n}Title`, e.target.value)} />
              </Field>
              <Field label="Organization" required half>
                <input className={iCls} placeholder="Memorial Hospital" required
                  value={form[`ref${n}Org`] || ''} onChange={(e) => set(`ref${n}Org`, e.target.value)} />
              </Field>
              <Field label="Relationship" required half>
                <select className={sCls} required
                  value={form[`ref${n}Relationship`] || ''} onChange={(e) => set(`ref${n}Relationship`, e.target.value)}>
                  <option value="" disabled>Select relationship</option>
                  <option>Supervising Physician</option>
                  <option>Department Chief</option>
                  <option>Colleague / Peer</option>
                  <option>Program Director</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Email" required half>
                <input type="email" className={iCls} placeholder="sarah@hospital.org" required
                  value={form[`ref${n}Email`] || ''} onChange={(e) => set(`ref${n}Email`, e.target.value)} />
              </Field>
              <Field label="Phone" half>
                <input type="tel" className={iCls} placeholder="(713) 000-0000"
                  value={form[`ref${n}Phone`] || ''} onChange={(e) => set(`ref${n}Phone`, e.target.value)} />
              </Field>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function StepMalpractice({ form, set }) {
  const hasClaims = form.hasMalpracticeClaims === 'yes';
  return (
    <>
      <SectionHeader
        step={4}
        title="Malpractice History & Acknowledgment"
        subtitle="Standard disclosures required for all locum placements."
      />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Current Malpractice Coverage" required>
          <select className={sCls} required
            value={form.malpracticeCoverage || ''} onChange={(e) => set('malpracticeCoverage', e.target.value)}>
            <option value="" disabled>Select</option>
            <option>Yes — occurrence-based</option>
            <option>Yes — claims-made</option>
            <option>No — will need coverage provided</option>
            <option>Unsure</option>
          </select>
        </Field>
        <Field label="Malpractice Claims History" required>
          <select className={sCls} required
            value={form.hasMalpracticeClaims || ''} onChange={(e) => set('hasMalpracticeClaims', e.target.value)}>
            <option value="" disabled>Select</option>
            <option value="no">No prior claims or settlements</option>
            <option value="yes">Yes — I have prior claims or settlements</option>
          </select>
        </Field>
        {hasClaims && (
          <Field label="Please briefly describe" hint="Nature, resolution, and current status of any claims">
            <textarea rows={4} className={iCls}
              placeholder="Describe the claim(s), outcome, and any relevant context…"
              value={form.malpracticeDetails || ''} onChange={(e) => set('malpracticeDetails', e.target.value)} />
          </Field>
        )}
        <Field label="License in Good Standing?" required>
          <select className={sCls} required
            value={form.licenseGoodStanding || ''} onChange={(e) => set('licenseGoodStanding', e.target.value)}>
            <option value="" disabled>Select</option>
            <option>Yes — all licenses in good standing</option>
            <option>No — there are restrictions or actions</option>
          </select>
        </Field>
        <Field label="Additional Notes" hint="Any other information relevant to your application">
          <textarea rows={3} className={iCls}
            placeholder="Anything else you'd like us to know…"
            value={form.additionalNotes || ''} onChange={(e) => set('additionalNotes', e.target.value)} />
        </Field>

        {/* Acknowledgment */}
        <div className="col-span-2 rounded-2xl border border-[#1e2d4a] bg-[#071325]/60 p-5 mt-2">
          <p className="text-xs font-bold uppercase tracking-widest text-[#c9a84c] mb-3">
            Applicant Acknowledgment
          </p>
          <p className="text-sm text-slate-400 leading-relaxed mb-4">
            By submitting this application, I confirm that all information provided is accurate and
            complete to the best of my knowledge. I understand that misrepresentation may result in
            disqualification or termination from the Lumosye clinician network.
          </p>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              required
              checked={form.acknowledged || false}
              onChange={(e) => set('acknowledged', e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[#1e2d4a] bg-[#070f1f] accent-[#c9a84c]"
            />
            <span className="text-sm text-slate-300 leading-relaxed">
              I acknowledge that the information in this application is accurate and I agree to
              Lumosye's terms for clinician network participation.
              <span className="text-[#c9a84c] ml-1">*</span>
            </span>
          </label>
        </div>
      </div>
    </>
  );
}

/* ─────────────────── MAIN PAGE ─────────────────────────────────────── */
export default function ClinicianApply() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [cvFile, setCvFile] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);
  const startedAtRef = useRef(Date.now());
  const topRef = useRef(null);

  const set = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  // Scroll to top on step change
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const canProceed = () => {
    if (step === 0) return form.firstName && form.lastName && form.email && form.phone && form.credential && form.city && form.state;
    if (step === 1) return form.specialty && form.yearsExperience && form.licensedStates && form.boardCertified;
    if (step === 2) return form.availability && form.shiftPreference && form.assignmentLength && form.willingToTravel;
    if (step === 3) return form.ref1Name && form.ref1Title && form.ref1Org && form.ref1Email && form.ref1Relationship
                        && form.ref2Name && form.ref2Title && form.ref2Org && form.ref2Email && form.ref2Relationship;
    if (step === 4) return form.malpracticeCoverage && form.hasMalpracticeClaims && form.licenseGoodStanding && form.acknowledged;
    return false;
  };

  const handleNext = () => {
    if (!canProceed()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canProceed()) return;
    setStatus('submitting');
    setErrorMsg('');

    const fillDurationMs = Date.now() - startedAtRef.current;

    try {
      let cvPath = '';
      let cvFileName = '';

      if (cvFile) {
        const tempId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const storageRef = ref(staffingStorage, `cvs/${tempId}/${cvFile.name}`);
        const snapshot = await uploadBytes(storageRef, cvFile);
        cvPath = snapshot.ref.fullPath;
        cvFileName = cvFile.name;
      }

      const submitFn = httpsCallable(staffingFunctions, 'submitStaffingInquiry');
      await submitFn({
        type: 'application',
        ...form,
        acknowledged: form.acknowledged ? 'Yes' : 'No',
        cvPath,
        cvFileName,
        fillDurationMs,
        website: '',
      });

      setStatus('success');
    } catch (err) {
      console.error('Application error:', err);
      setErrorMsg('Something went wrong. Please try again or email us directly.');
      setStatus('error');
    }
  };

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: 'linear-gradient(160deg, #040d1c 0%, #071325 50%, #050e1f 100%)' }}
    >
      {/* Grid texture */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.02]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg,#ffffff 0px,#ffffff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#ffffff 0px,#ffffff 1px,transparent 1px,transparent 40px)' }} />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] pointer-events-none z-0"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.06) 0%, transparent 70%)' }} />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#1e2d4a]/80 backdrop-blur-xl bg-[#040d1c]/80">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between max-w-4xl">
          <Link to="/medical-staffing" className="flex items-center gap-3">
            <img src={lumosyeLogo} alt="Lumosye" className="h-9 w-auto object-contain transition-transform duration-300 hover:scale-[1.03]" />
            <span className="hidden sm:flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 border-l border-[#1e2d4a] pl-3">
              <ChevronRight size={12} />
              Clinician Application
            </span>
          </Link>
          <Link to="/medical-staffing"
            className="text-xs text-slate-500 hover:text-[#c9a84c] transition">
            ← Back to Staffing
          </Link>
        </nav>
      </header>

      <main className="relative z-10 py-12 md:py-16 px-6">
        <div className="max-w-2xl mx-auto" ref={topRef}>

          {/* Page title */}
          {step === 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#c9a84c]" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a84c]">
                  Lumosye Medical Staffing
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-3"
                style={{ fontFamily: "'Georgia', serif" }}>
                Clinician Network
                <br /><span style={{ color: '#c9a84c' }}>Application</span>
              </h1>
              <p className="text-slate-400 leading-relaxed">
                Join a physician-run locum network built around flexibility, transparent communication,
                and assignments that actually fit your schedule. Takes about 5–8 minutes.
              </p>
            </div>
          )}

          {/* SUCCESS */}
          {status === 'success' ? (
            <div className="rounded-3xl border border-[#1e2d4a] bg-[#0b1628] p-10 text-center">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#c9a84c]/10 border border-[#c9a84c]/30 mb-5">
                <CheckCircle2 size={30} className="text-[#c9a84c]" />
              </div>
              <h2 className="text-2xl font-black text-white mb-3" style={{ fontFamily: "'Georgia', serif" }}>
                Application received
              </h2>
              <p className="text-slate-400 leading-relaxed mb-2">
                Thank you for applying to the Lumosye clinician network. We'll review your
                application and reach out within a few business days.
              </p>
              <p className="text-xs text-slate-500 mb-8">
                Questions? Email us at{' '}
                <a href={`mailto:${EMAIL}`} className="text-[#c9a84c] hover:underline">{EMAIL}</a>
              </p>
              <Link to="/medical-staffing"
                className="inline-flex items-center gap-2 rounded-full border border-[#1e2d4a] px-7 py-3 text-sm font-semibold text-white hover:border-[#c9a84c]/40 transition">
                ← Back to Medical Staffing
              </Link>
            </div>
          ) : (
            <div className="rounded-3xl border border-[#1e2d4a] bg-[#0b1628] p-8 md:p-10">
              <ProgressBar current={step} total={STEPS.length} />

              <form onSubmit={step === STEPS.length - 1 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>

                {/* Honeypot */}
                <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                  <input name="website" type="text" tabIndex="-1" autoComplete="off"
                    value={form.website || ''} onChange={(e) => set('website', e.target.value)} />
                </div>

                {step === 0 && <StepPersonal form={form} set={set} cvFile={cvFile} setCvFile={setCvFile} fileInputRef={fileInputRef} />}
                {step === 1 && <StepSpecialty form={form} set={set} />}
                {step === 2 && <StepAvailability form={form} set={set} />}
                {step === 3 && <StepReferences form={form} set={set} />}
                {step === 4 && <StepMalpractice form={form} set={set} />}

                {status === 'error' && (
                  <p className="mt-4 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                    {errorMsg}{' '}
                    <a href={`mailto:${EMAIL}`} className="underline">{EMAIL}</a>
                  </p>
                )}

                {/* Nav buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#1e2d4a]">
                  {step > 0 ? (
                    <button type="button" onClick={handleBack}
                      className="inline-flex items-center gap-2 rounded-full border border-[#1e2d4a] px-6 py-3 text-sm font-semibold text-slate-300 hover:border-[#c9a84c]/40 hover:text-white transition">
                      <ArrowLeft size={15} /> Back
                    </button>
                  ) : <div />}

                  <button
                    type="submit"
                    disabled={!canProceed() || status === 'submitting'}
                    className="inline-flex items-center gap-2.5 rounded-full bg-[#c9a84c] px-7 py-3 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] disabled:opacity-40 disabled:cursor-not-allowed transition shadow-lg shadow-[#c9a84c]/20"
                  >
                    {status === 'submitting' ? (
                      <><Loader2 size={15} className="animate-spin" /> Submitting…</>
                    ) : step === STEPS.length - 1 ? (
                      <>Submit Application <ArrowRight size={15} /></>
                    ) : (
                      <>Continue <ArrowRight size={15} /></>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#1e2d4a] py-6 mt-8">
        <div className="container mx-auto px-6 max-w-4xl flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
          <span>© {new Date().getFullYear()} Lumosye Health Services LLC</span>
          <div className="flex items-center gap-4">
            <Link to="/medical-staffing" className="hover:text-slate-400 transition">Medical Staffing</Link>
            <a href={`mailto:${EMAIL}`} className="hover:text-slate-400 transition">{EMAIL}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}